import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa';

const EventsSection = ({ activeTab, userId }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('recent');
  const [showSortMessage, setShowSortMessage] = useState(false);

  const toggleSort = () => {
    const newOrder = sortOrder === 'recent' ? 'oldest' : 'recent';
    setSortOrder(newOrder);
    setShowSortMessage(true);
    setTimeout(() => setShowSortMessage(false), 1500);
  };

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

  const getCurrentTab = () => {
    const tabPart = activeTab.split('-')[1];
    return tabPart || 'attended';
  };

  useEffect(() => {
    fetchEvents();
  }, [activeTab, userId]);

  const fetchEvents = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const tab = getCurrentTab();

    try {
      // For created events, use different endpoint
      if (tab === 'created') {
        const res = await fetch(`${API_BASE}/events/user/my-created-events`, { headers });
        const data = await res.json();

        if (data.success) {
          // Combine upcoming and past created events
          const allCreated = [...(data.data?.upcoming || []), ...(data.data?.past || [])];
          setEvents(allCreated);
        } else {
          setEvents([]);
        }
        setLoading(false);
        return;
      }

      // Use the unified my-events endpoint for attended and liked tabs
      const res = await fetch(`${API_BASE}/events/user/my-events`, { headers });
      const data = await res.json();

      if (data.success) {
        const now = new Date();
        let filteredEvents = [];

        if (tab === 'liked') {
          filteredEvents = data.data?.favorites || [];
        } else if (tab === 'attended') {
          filteredEvents = data.data?.attended || [];
        }

        setEvents(filteredEvents);
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    return `${startStr} - ${endStr}`;
  };

  const renderTabContent = () => {
    const tab = getCurrentTab();

    if (loading) {
      return (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      );
    }

    if (events.length === 0) {
      const emptyStates = {
        attended: {
          icon: 'bi-calendar-check',
          title: 'No events yet',
          description: 'Events you\'re attending or have attended will appear here.',
          action: { label: 'Browse Events', path: '/events' },
        },
        created: {
          icon: 'bi-plus-circle',
          title: 'No created events',
          description: 'Events you\'ve added to the platform will appear here.',
          action: { label: 'Add Event', path: '/events/import' },
        },
        liked: {
          icon: 'bi-heart',
          title: 'No liked events yet',
          description: 'Events you like will appear here. Browse events and hit the heart!',
          action: { label: 'Browse Events', path: '/events' },
        },
      };

      const state = emptyStates[tab] || emptyStates.attended;

      return (
        <div className="empty-state">
          <i className={`bi ${state.icon}`}></i>
          <h3>{state.title}</h3>
          <p>{state.description}</p>
          {state.action && (
            <button className="primary-btn" onClick={() => navigate(state.action.path)}>
              {state.action.label}
            </button>
          )}
        </div>
      );
    }

    const sortedEvents = [...events].sort((a, b) => {
      const dateA = new Date(a.startDateTime || a.createdAt);
      const dateB = new Date(b.startDateTime || b.createdAt);
      return sortOrder === 'recent' ? dateB - dateA : dateA - dateB;
    });

    return (
      <>
        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
          {showSortMessage && (
            <span style={{ fontSize: '0.8rem', color: '#6B7280', animation: 'fadeIn 0.2s ease-out' }}>
              {sortOrder === 'recent' ? 'Most recent' : 'Oldest first'}
            </span>
          )}
          <button
            onClick={toggleSort}
            title={sortOrder === 'recent' ? 'Showing most recent' : 'Showing oldest first'}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
              color: '#9CA3AF', display: 'flex', alignItems: 'center', transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#9CA3AF'}
          >
            <i className={`bi ${sortOrder === 'recent' ? 'bi-sort-down' : 'bi-sort-up'}`} style={{ fontSize: '1.1rem' }}></i>
          </button>
        </div>
        <div className="events-grid">
        {sortedEvents.map((event) => {
          const isPast = new Date(event.endDateTime || event.startDateTime) < new Date();
          const rating = isPast && event.averageRating ? event.averageRating : null;
          return (
          <div key={event._id} className={`event-card ${isPast ? 'event-card--past' : 'event-card--future'}`} onClick={() => navigate(`/event/${event._id}`)}>
            <div className="event-image">
              {event.eventImage ? (
                <img src={event.eventImage} alt={event.title} />
              ) : (
                <div className="placeholder-image">
                  <i className="bi bi-calendar-event"></i>
                </div>
              )}
              {!isPast && (
                <span className="event-coming-up">Coming Up</span>
              )}
              {rating && (
                <div className="event-rating-badge">
                  {[...Array(5)].map((_, i) => {
                    const full = Math.floor(rating);
                    const hasHalf = rating % 1 >= 0.25 && rating % 1 < 0.75;
                    if (i < full) return <FaStar key={i} className="star-filled" />;
                    if (i === full && hasHalf) return <FaStarHalfAlt key={i} className="star-filled" />;
                    return <FaRegStar key={i} className="star-empty" />;
                  })}
                </div>
              )}
              {tab === 'created' && (
                <button
                  className="edit-event-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/events/edit/${event._id}`);
                  }}
                  title="Edit event"
                >
                  <i className="bi bi-pencil"></i>
                </button>
              )}
            </div>
            <div className="event-info">
              <h4>{event.title}</h4>
              <div className="event-meta">
                <span className="event-date">
                  <i className="bi bi-calendar3"></i>
                  {event.endDateTime ? formatDateRange(event.startDateTime, event.endDateTime) : formatDate(event.startDateTime)}
                </span>
                {event.location && (
                  <span className="event-location">
                    <i className="bi bi-geo-alt"></i>
                    {event.location}
                  </span>
                )}
              </div>
              {tab === 'attended' && isPast && event.userReview && (
                <div className="event-review-badge">
                  <i className="bi bi-chat-quote"></i> You reviewed this event
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>
      </>
    );
  };

  const getTabTitle = () => {
    const titles = {
      attended: 'My Events',
      created: 'Created Events',
      liked: 'Liked Events',
    };
    return titles[getCurrentTab()] || 'My Events';
  };

  const getTabDescription = () => {
    const descriptions = {
      attended: 'Events you\'re attending and have attended',
      created: 'Events you\'ve added to the platform',
      liked: 'Events you\'ve liked across the platform',
    };
    return descriptions[getCurrentTab()] || '';
  };

  const tab = getCurrentTab();

  return (
    <div className="section-content events-section">
      <div className="section-header">
        <div className="header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', width: '100%' }}>
          <div>
            <h1>{getTabTitle()}</h1>
            <p className="section-subtitle">{getTabDescription()}</p>
          </div>

        </div>
      </div>
      {renderTabContent()}
    </div>
  );
};

export default EventsSection;
